// Methods for a real patient beside the model fitted to them: the seizure and how it was
// recorded, the connectome and the fit, how the model is read out at the contacts and how the two
// line up in time. All of it is the run's own: its header, model.json and observation.json, with the HCP credit a connectome
// built from HCP subjects carries (hcpCredit).

import { useAtlas } from '../app/store.ts';
import { openedSlot } from '../app/sims.ts';
import type { DynamicsSim } from '../data/sims/dynamics.ts';
import { mainSlot } from '../app/scene.ts';
import { modalityOf, openComparison, type ModelJson } from '../data/sims/comparison.ts';
import { formatValue } from '../ui/chart.ts';
import { Ref } from './Methods.tsx';
import { useLoaded } from './Traces.tsx';
import { regionName } from './PatientTraces.tsx';

/** A connectome source that names HCP, with the Human Connectome Project's credit where it lacks it (the lead's ruling, #2 6026501702). */
export const hcpCredit = (source: string): string => (/\bHCP\b/.test(source) && !/Human Connectome Project/.test(source) ? `${source}, from Human Connectome Project (WU-Minn) data` : source);

/** An OpenNeuro dataset id in a source line, as a link to the dataset. */
function Source({ text }: { text: string }) {
  const ds = /\bds\d{6}\b/.exec(text)?.[0];
  return ds ? (
    <a href={`https://openneuro.org/datasets/${ds}`} target="_blank" rel="noreferrer">
      {text}
    </a>
  ) : (
    <span>{text}</span>
  );
}

export function PatientMethods() {
  const o = openedSlot('main');
  const sim = o?.format === 'dynamics' ? (o.sim as DynamicsSim) : null;
  const catalogue = useAtlas((s) => s.catalogue);
  const modality = useAtlas((s) => modalityOf(mainSlot(s)?.annotation?.label));
  const loaded = useLoaded(() => (sim ? Promise.all([openComparison(sim), sim.files.maybeJson<ModelJson>('model.json')]) : null), [sim]);
  if (!sim || !loaded.value) return null;
  const [c, model] = loaded.value;
  const obs = c.observation.observed;
  // The contacts ElectrodeSites draws: those observation.json places.
  const placed = c.contacts.filter((k) => k.position).length;
  const soz = c.contacts.filter((k) => k.position && k.soz).length;
  const fit = model?.inference;
  const time = model?.time_mapping;
  const refs = model?.references ?? [];
  return (
    <section className="methods-sim">
      <h5 className="caps">Patient vs model</h5>
      <dl>
        <dt>Patient</dt>
        <dd>
          {c.patient ?? 'Not named'}
          {obs?.seizure ? <span className="muted mono">{` ${obs.seizure}`}</span> : null}
        </dd>
        <dt>Recording</dt>
        <dd>
          {`${modality ? `${modality}: ` : ''}${c.contacts.length} contacts, as bipolar pairs${obs?.feature ? ` · ${obs.feature}` : ''}`}
          {obs?.source ? (
            <div className="muted">
              <Source text={obs.source} />
            </div>
          ) : null}
        </dd>
        {placed ? (
          <>
            <dt>On the picture</dt>
            <dd>{`Their ${placed} contacts at their bipolar pairs' midpoints (observation.json, the run's surface RAS, drawn as MNI as the model's sensors are), each lit by its recorded envelope at the scene time less the model's onset, on the recording's 99.5th percentile. Ringed: the ${soz} in the seizure onset zone their clinicians marked.`}</dd>
          </>
        ) : null}
        {model?.connectome?.source ? (
          <>
            <dt>Connectome</dt>
            <dd>
              {hcpCredit(model.connectome.source)}
              {model.connectome.doi ? (
                <div className="muted">
                  <Ref text={`doi:${model.connectome.doi}`} />
                </div>
              ) : null}
            </dd>
          </>
        ) : null}
        {fit?.method ? (
          <>
            <dt>Fit</dt>
            <dd>{`Excitability of every node: ${fit.method}${fit.n_seizures ? `, from ${fit.n_seizures} of their seizures` : ''}`}</dd>
          </>
        ) : null}
        {c.observation.model ? (
          <>
            <dt>At contacts</dt>
            <dd>
              {c.observation.model}
              {c.observation.gain ? <div className="muted">{`Gain: ${c.observation.gain}`}</div> : null}
            </dd>
          </>
        ) : null}
        <dt>Timing</dt>
        <dd>
          {`The model's onset (${formatValue(c.onset)} s${time?.leading_parcel ? `, ${regionName(sim, catalogue, time.leading_parcel)} first` : ''}) lines up with the clinical onset.`}
          {time?.seconds_per_model_unit && time.observed_median_seizure_s ? (
            <div className="muted">{`${formatValue(time.seconds_per_model_unit)} s per model time unit, so the model's seizure lasts their median ${formatValue(time.observed_median_seizure_s)} s`}</div>
          ) : null}
        </dd>
        {refs.length ? (
          <>
            <dt>References</dt>
            <dd>
              <ul className="methods-inputs">
                {refs.map((r) => (
                  <li key={r}>
                    <Ref text={r} />
                  </li>
                ))}
              </ul>
            </dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}
